import { useCallback, useRef, useState } from 'react';
import { KAYTTOIKA_DATASETS } from './services/kayttoikaDatasets';
import { useRTKayttoikaVastaukset } from './services/useRTKayttoikaVastaukset';
import RTKayttoikaSidebar from './views/RTKayttoikaSidebar';
import RTKayttoikaMain, { leafAnchorId } from './views/RTKayttoikaMain';

// Shared questionnaire component — the single piece of UI reused both as
// the Taloyhtiökortti "Järjestelmäkuvaus" subtab content (full page) and
// inside RTKayttoikaModal from the report editor, same relationship
// ValmislauseBrowser has to the admin page / ValmislauseModal. Only prop
// is kiinteistoId — everything else (nav state, answers) is owned here.
//
// Two questionnaires share this one component — Rakennustekniikka
// (RT103765) and Talotekniikka (RT103766 + its referenced Sähkö/
// Tietotekniikka lifespans) — both selectable from the same sidebar. They
// share one answers map per kiinteistö (question codes never collide
// across datasets, see kayttoikaDatasets.js), so switching never loses or
// crosses an answer.
//
// Table-of-contents layout (replaces the earlier dataset-pills + single-
// open-leaf accordion): a sidebar lists dataset → group → leaf section at
// once, and picking a leaf scrolls the main column to it rather than
// hiding/revealing content — every leaf of the active group stays
// rendered, same relationship ReportSidebar.jsx has to the report body it
// jumps around in. Desktop: sticky rail. Mobile: the same sidebar content
// in a slide-in drawer behind a hamburger button, same hand-rolled
// translateX mechanism ReportSidebar.jsx uses (no Offcanvas component —
// matches the rest of this codebase). Both the rail and the drawer use
// `position: sticky` / `position: fixed` relative to the page's own
// scroll — this component is also used inside RTKayttoikaModal, whose
// comment warns against adding a second nested scroll container, so
// neither the rail nor the main column gets its own `overflowY: auto`.
export default function RTKayttoikaKysely({ kiinteistoId, rakennuksenIka }) {
  const { answers, toggleOption, pickRasitusluokka } = useRTKayttoikaVastaukset(kiinteistoId);
  const [activeDatasetKey, setActiveDatasetKey] = useState(KAYTTOIKA_DATASETS[0].key);
  const activeDataset = KAYTTOIKA_DATASETS.find(d => d.key === activeDatasetKey) ?? KAYTTOIKA_DATASETS[0];

  const [activeGroup, setActiveGroup] = useState(activeDataset.tree[0]?.group ?? null);
  const [activeLeafIndex, setActiveLeafIndex] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Scroll-spy (see RTKayttoikaMain) reports whichever leaf is currently
  // at the top of the viewport as the user scrolls by hand. A sidebar
  // click also scrolls programmatically, which would otherwise fight the
  // scroll-spy mid-animation — it'd briefly report every leaf it scrolls
  // past on the way. suppressSpyUntilRef mutes scroll-spy updates for a
  // moment after a programmatic scroll starts, long enough for the
  // smooth-scroll to land.
  const suppressSpyUntilRef = useRef(0);
  const scrollToLeaf = (li) => {
    suppressSpyUntilRef.current = Date.now() + 700;
    requestAnimationFrame(() => {
      document.getElementById(leafAnchorId(li))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const handleVisibleLeafChange = useCallback((li) => {
    if (Date.now() < suppressSpyUntilRef.current) return;
    setActiveLeafIndex(li);
  }, []);

  const handleSelectDataset = (key) => {
    setActiveDatasetKey(key);
    const dataset = KAYTTOIKA_DATASETS.find(d => d.key === key);
    setActiveGroup(dataset?.tree[0]?.group ?? null);
    setActiveLeafIndex(0);
  };

  // A group click only expands its leaves within the same open drawer —
  // closing it here forced a re-open after every step drilling down. A
  // leaf click is the real destination (its questions render in main),
  // so that's the one that closes it.
  const handleSelectGroup = (group) => {
    setActiveGroup(group);
    setActiveLeafIndex(0);
    scrollToLeaf(0);
  };

  const handleSelectLeaf = (li) => {
    setActiveLeafIndex(li);
    setMobileOpen(false);
    scrollToLeaf(li);
  };

  const activeGroupEntry = activeDataset.tree.find(g => g.group === activeGroup);

  const sidebarProps = {
    datasets: KAYTTOIKA_DATASETS, activeDatasetKey, activeGroup, activeLeafIndex, answers,
    onSelectDataset: handleSelectDataset, onSelectGroup: handleSelectGroup, onSelectLeaf: handleSelectLeaf,
  };

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: 16, display: 'flex', gap: 22, alignItems: 'flex-start' }}>
      <div className="d-none d-md-block" style={{
        width: 230, flexShrink: 0, position: 'sticky', top: 0,
        background: '#fff', border: '1px solid #e5e5ea', borderRadius: 14, overflow: 'hidden',
      }}>
        <RTKayttoikaSidebar {...sidebarProps} />
      </div>

      <div className="d-md-none" style={{
        position: 'fixed', top: 0, left: 0, bottom: 0, width: '80%', maxWidth: 270, zIndex: 1200,
        background: '#fff', boxShadow: '2px 0 10px rgba(0,0,0,0.25)', overflowY: 'auto',
        transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.22s ease',
      }}>
        <div className="d-flex justify-content-end p-2">
          <button type="button" onClick={() => setMobileOpen(false)} className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
            style={{ width: 28, height: 28, padding: 0, borderRadius: 7 }}>✕</button>
        </div>
        <RTKayttoikaSidebar {...sidebarProps} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="d-flex d-md-none align-items-center gap-2" style={{
          position: 'sticky', top: 0, background: '#fff', zIndex: 10, padding: '2px 0 10px',
        }}>
          <button type="button" onClick={() => setMobileOpen(true)} style={{
            width: 32, height: 32, borderRadius: 8, background: '#198754', color: '#fff',
            border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem',
          }}>☰</button>
          <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>Käyttöiät ja kunnossapito</div>
        </div>

        {/* Some lifespan values aren't years — R/J/"Saavutettu" are the RT
            card's own symbols (see build_questionnaire.py's footnote on the
            source Excel), meaningless without this line — kept short and
            muted since it's shown above every view, not just once. */}
        <div style={{ fontSize: '0.68rem', color: '#adb5bd', lineHeight: 1.5, marginBottom: 16 }}>
          R = rakennuksen ikä · J = järjestelmän ikä · "Saavutettu" = käyttöikä jo ylitetty
        </div>

        <RTKayttoikaMain
          groupEntry={activeGroupEntry}
          answers={answers}
          rakennuksenIka={rakennuksenIka}
          onToggleOption={toggleOption}
          onPickRasitusluokka={pickRasitusluokka}
          onVisibleLeafChange={handleVisibleLeafChange}
        />
      </div>
    </div>
  );
}
